import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { exportToCSV } from '../utils/csvExport';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Package,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  CircleNotch,
  Upload,
  Download,
  FileArrowUp,
  CheckCircle,
  XCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialProductForm = {
  product_code: '', sku: '', name: '', description: '', category: '', brand: '',
  unit: 'pcs', barcode: '', cost_price: '', selling_price: '', tax: '0', reorder_level: '10', image: '', active: true
};

export default function Products() {
  const { hasPermission } = useAuth();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(initialProductForm);
  const [saving, setSaving] = useState(false);
  const [dialogTab, setDialogTab] = useState('single');

  // Bulk upload state
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const canAddProduct = hasPermission('add_product');
  const canEditProduct = hasPermission('edit_product');
  const canViewCost = hasPermission('view_cost');

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      const res = await axios.get(`${API_URL}/api/products?${params}`, { withCredentials: true });
      setProducts(res.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally { setLoading(false); }
  }, [search, categoryFilter]);

  const fetchFilters = async () => {
    try {
      const [catRes, brandRes] = await Promise.all([
        axios.get(`${API_URL}/api/categories`, { withCredentials: true }),
        axios.get(`${API_URL}/api/brands`, { withCredentials: true }),
      ]);
      setCategories(catRes.data);
      setBrands(brandRes.data);
    } catch (error) { console.error('Error fetching filters:', error); }
  };

  useEffect(() => { fetchProducts(); fetchFilters(); }, [fetchProducts]);

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        product_code: product.product_code || '', sku: product.sku || '', name: product.name || '',
        description: product.description || '', category: product.category || '', brand: product.brand || '',
        unit: product.unit || 'pcs', barcode: product.barcode || '',
        cost_price: product.cost_price?.toString() || '', selling_price: product.selling_price?.toString() || '',
        tax: product.tax?.toString() || '0', reorder_level: product.reorder_level?.toString() || '10',
        image: product.image || '', active: product.active !== false
      });
      setDialogTab('single');
    } else {
      setEditingProduct(null);
      setFormData(initialProductForm);
      setDialogTab('single');
      setBulkFile(null);
      setBulkResult(null);
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => { setShowDialog(false); setEditingProduct(null); setFormData(initialProductForm); setBulkFile(null); setBulkResult(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...formData, cost_price: parseFloat(formData.cost_price) || 0, selling_price: parseFloat(formData.selling_price) || 0, tax: parseFloat(formData.tax) || 0, reorder_level: parseInt(formData.reorder_level) || 10 };
    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, payload, { withCredentials: true });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API_URL}/api/products`, payload, { withCredentials: true });
        toast.success('Product created successfully');
      }
      handleCloseDialog(); fetchProducts(); fetchFilters();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save product';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save product');
    } finally { setSaving(false); }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, { withCredentials: true });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) { toast.error('Failed to delete product'); }
  };

  const handleBulkDownloadTemplate = () => {
    const csvContent = "product_code,name,category,brand,unit,barcode,cost_price,selling_price,tax,reorder_level\nAP001,Brake Pad Set,Brakes,Bosch,pcs,,25.00,45.00,0,10";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_template.csv';
    a.click();
    toast.success('Template downloaded!');
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFile) return;
    setBulkUploading(true);
    try {
      const text = await bulkFile.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { toast.error('File is empty or has no data rows'); setBulkUploading(false); return; }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      let successCount = 0;
      const errors = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
        try {
          await axios.post(`${API_URL}/api/products`, {
            product_code: row.product_code || `P${Date.now()}${i}`,
            name: row.name || `Product ${i}`,
            category: row.category || '',
            brand: row.brand || '',
            unit: row.unit || 'pcs',
            barcode: row.barcode || '',
            cost_price: parseFloat(row.cost_price) || 0,
            selling_price: parseFloat(row.selling_price) || 0,
            tax: parseFloat(row.tax) || 0,
            reorder_level: parseInt(row.reorder_level) || 10,
          }, { withCredentials: true });
          successCount++;
        } catch (err) {
          errors.push({ row: i + 1, error: err.response?.data?.detail || 'Failed' });
        }
      }
      setBulkResult({ success: successCount, errors });
      if (successCount > 0) { toast.success(`${successCount} products uploaded!`); fetchProducts(); fetchFilters(); }
    } catch (error) {
      toast.error('Failed to process file');
    } finally { setBulkUploading(false); }
  };

  const getStockStatus = (product) => {
    if (product.current_stock === 0) return { label: t('outOfStock'), class: 'badge-out-of-stock' };
    if (product.current_stock <= product.reorder_level) return { label: t('lowStock'), class: 'badge-low-stock' };
    return { label: t('inStock'), class: 'badge-in-stock' };
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>{t('products')}</h1>
          <p className="text-[var(--dm-text-muted)] mt-1">{t('manageProductCatalog')}</p>
        </div>
        {canAddProduct && (
          <div className="flex gap-2">
            <Button onClick={() => exportToCSV(products.map(p => ({
              Name: p.name, Code: p.product_code, SKU: p.sku, Category: p.category, Brand: p.brand,
              Cost_Price: p.cost_price, Selling_Price: p.selling_price, Tax: p.tax, Stock: p.current_stock,
              Reorder_Level: p.reorder_level, Active: p.active
            })), 'products')} data-testid="export-products-btn"
              className="bg-[var(--dm-bg-card-hover)] text-[var(--text)] font-semibold hover:bg-[var(--dm-border)] rounded-xl h-11 border border-[var(--dm-border)]">
              <Download size={20} className="mr-2" />CSV
            </Button>
            <Button onClick={() => handleOpenDialog()} data-testid="add-product-btn"
              className="bg-black text-white font-semibold hover:bg-black/80 rounded-xl h-11 border border-[var(--dm-border)]">
              <Plus size={20} className="mr-2" />{t('addProduct')}
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--dm-text-muted)]" />
          <Input placeholder={t('searchProductsShort')} value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="product-search" className="pl-10 bg-[var(--card)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-11" />
        </div>
        <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-48 bg-[var(--card)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-11" data-testid="category-filter">
            <SelectValue placeholder={t('allCategories')} />
          </SelectTrigger>
          <SelectContent className="bg-[var(--card)] border-[var(--dm-border)]">
            <SelectItem value="all">{t('allCategories')}</SelectItem>
            {categories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[var(--card)] border border-[var(--dm-border)] overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full table-dm">
            <thead>
              <tr>
                <th className="text-left p-4">Code</th>
                <th className="text-left p-4">{t('productName')}</th>
                <th className="text-left p-4">{t('category')}</th>
                {canViewCost && <th className="text-right p-4">{t('costPrice')}</th>}
                <th className="text-right p-4">{t('price')}</th>
                <th className="text-right p-4">Stock</th>
                <th className="text-center p-4">{t('status')}</th>
                {canEditProduct && <th className="text-center p-4">{t('actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canViewCost ? 8 : 7} className="p-8 text-center"><CircleNotch size={24} className="animate-spin text-[var(--primary)] mx-auto" /></td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan={canViewCost ? 8 : 7} className="p-8 text-center text-[var(--dm-text-muted)]"><Package size={48} className="mx-auto mb-2 opacity-50" /><p>{t('noProductsFound')}</p></td></tr>
              ) : (
                products.map((product) => {
                  const status = getStockStatus(product);
                  return (
                    <tr key={product.id} data-testid={`product-row-${product.id}`}>
                      <td className="p-4 font-mono text-[var(--primary)]">{product.product_code}</td>
                      <td className="p-4"><p className="text-[var(--text)] font-medium">{product.name}</p>{product.brand && <p className="text-xs text-[var(--dm-text-muted)]">{product.brand}</p>}</td>
                      <td className="p-4 text-[var(--dm-text-muted)]">{product.category || '-'}</td>
                      {canViewCost && <td className="p-4 text-right font-mono text-[var(--dm-text-muted)]">${product.cost_price?.toFixed(2)}</td>}
                      <td className="p-4 text-right font-mono text-[var(--text)]">${product.selling_price?.toFixed(2)}</td>
                      <td className="p-4 text-right font-mono text-[var(--text)]">{product.current_stock}</td>
                      <td className="p-4 text-center"><span className={`px-2 py-1 text-xs font-medium rounded ${status.class}`}>{status.label}</span></td>
                      {canEditProduct && (
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => handleOpenDialog(product)} data-testid={`edit-product-${product.id}`} className="p-2 text-[var(--dm-text-muted)] hover:text-[var(--primary)] transition-colors"><PencilSimple size={18} /></button>
                            <button onClick={() => handleDelete(product.id)} data-testid={`delete-product-${product.id}`} className="p-2 text-[var(--dm-text-muted)] hover:text-[#EF4444] transition-colors"><Trash size={18} /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Dialog with Add Single / Upload Tabs */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[var(--card)] border-[var(--dm-border)] text-[var(--text)] max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>
              {editingProduct ? t('editProduct') : t('addNewProduct')}
            </DialogTitle>
          </DialogHeader>

          {!editingProduct && (
            <Tabs value={dialogTab} onValueChange={setDialogTab} className="mt-2">
              <TabsList className="bg-[var(--bg)] border border-[var(--dm-border)] p-1 w-full grid grid-cols-2">
                <TabsTrigger value="single" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg" data-testid="tab-single-product">
                  <Plus size={16} className="mr-2" />{t('addProduct')}
                </TabsTrigger>
                <TabsTrigger value="bulk" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg" data-testid="tab-bulk-upload">
                  <Upload size={16} className="mr-2" />{t('uploadProductList')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="single" className="mt-4">
                <ProductForm formData={formData} setFormData={setFormData} categories={categories} brands={brands}
                  saving={saving} onSubmit={handleSubmit} onCancel={handleCloseDialog} editingProduct={editingProduct} t={t} />
              </TabsContent>

              <TabsContent value="bulk" className="mt-4">
                <div className="space-y-4">
                  <Button type="button" variant="outline" onClick={handleBulkDownloadTemplate} data-testid="download-product-template-btn"
                    className="w-full h-12 border-[var(--dm-border)] text-[var(--primary)] hover:bg-[var(--primary)]/10 rounded-xl">
                    <Download size={20} className="mr-2" />{t('downloadProductTemplate')}
                  </Button>
                  <form onSubmit={handleBulkUpload} className="space-y-4">
                    <Input type="file" accept=".csv" onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }}
                      data-testid="bulk-file-input"
                      className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12 file:bg-black file:text-[var(--text)] file:border-0 file:mr-4 file:px-4 file:h-full file:rounded-l-xl" />
                    <Button type="submit" disabled={bulkUploading || !bulkFile} data-testid="bulk-upload-btn"
                      className="w-full h-12 bg-black text-white font-bold rounded-xl border border-[var(--dm-border)] hover:bg-black/80 disabled:opacity-50">
                      {bulkUploading ? <CircleNotch size={20} className="animate-spin" /> : <><FileArrowUp size={20} className="mr-2" />{t('uploadProductList')}</>}
                    </Button>
                  </form>
                  {bulkResult && (
                    <div className="p-4 bg-[var(--bg)] border border-[var(--dm-border)] rounded-xl">
                      <div className="flex items-center gap-3 mb-2"><CheckCircle size={20} className="text-[var(--primary)]" /><span className="text-[var(--primary)]">{bulkResult.success} products created</span></div>
                      {bulkResult.errors?.length > 0 && (
                        <div><div className="flex items-center gap-3 mb-2"><XCircle size={20} className="text-[#EF4444]" /><span className="text-[#EF4444]">{bulkResult.errors.length} errors</span></div>
                          <div className="max-h-32 overflow-y-auto text-xs text-[var(--dm-text-muted)]">
                            {bulkResult.errors.map((err, i) => <p key={i}>Row {err.row}: {err.error}</p>)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}

          {editingProduct && (
            <ProductForm formData={formData} setFormData={setFormData} categories={categories} brands={brands}
              saving={saving} onSubmit={handleSubmit} onCancel={handleCloseDialog} editingProduct={editingProduct} t={t} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductForm({ formData, setFormData, categories, brands, saving, onSubmit, onCancel, editingProduct, t }) {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('productCode')} *</Label>
          <Input value={formData.product_code} onChange={(e) => setFormData({ ...formData, product_code: e.target.value })}
            required data-testid="product-code-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl" placeholder="AP001" />
        </div>
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('sku')}</Label>
          <Input value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
            data-testid="product-sku-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl" placeholder="SKU001" />
        </div>
      </div>
      <div>
        <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('productName')} *</Label>
        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required data-testid="product-name-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl" placeholder="Brake Pad Set" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('category')}</Label>
          <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            data-testid="product-category-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl" placeholder="Brakes" list="categories-list" />
          <datalist id="categories-list">{categories.map((cat) => <option key={cat} value={cat} />)}</datalist>
        </div>
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('brand')}</Label>
          <Input value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
            data-testid="product-brand-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl" placeholder="Bosch" list="brands-list" />
          <datalist id="brands-list">{brands.map((brand) => <option key={brand} value={brand} />)}</datalist>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('costPrice')} *</Label>
          <Input type="number" step="0.01" min="0" value={formData.cost_price} onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
            required data-testid="product-cost-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl" placeholder="25.00" />
        </div>
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('sellingPrice')} *</Label>
          <Input type="number" step="0.01" min="0" value={formData.selling_price} onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })}
            required data-testid="product-price-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl" placeholder="45.00" />
        </div>
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('tax')} %</Label>
          <Input type="number" step="0.01" min="0" value={formData.tax} onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
            data-testid="product-tax-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl" placeholder="0" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('reorderLevel')}</Label>
          <Input type="number" min="0" value={formData.reorder_level} onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })}
            data-testid="product-reorder-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl" placeholder="10" />
        </div>
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('barcode')}</Label>
          <Input value={formData.barcode} onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
            data-testid="product-barcode-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl" placeholder="1234567890123" />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-[var(--dm-border)]">
        <Button type="button" variant="outline" onClick={onCancel} className="border-[var(--dm-border)] text-[var(--dm-text-muted)] hover:text-[var(--text)] rounded-xl">{t('cancel')}</Button>
        <Button type="submit" disabled={saving} data-testid="save-product-btn"
          className="bg-black text-white font-semibold hover:bg-black/80 rounded-xl border border-[var(--dm-border)]">
          {saving ? <CircleNotch size={20} className="animate-spin" /> : t('saveProduct')}
        </Button>
      </div>
    </form>
  );
}
